import { getLocale, t, tn, useI18n } from '../../i18n';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  FiAlertTriangle,
  FiArchive,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiEdit2,
  FiMail,
  FiMapPin,
  FiMoreHorizontal,
  FiPhone,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiSunset,
  FiTrash2,
  FiUser,
  FiUsers,
} from 'react-icons/fi';
import {
  clientService,
  type ClientContractRow,
} from '../../services/clientService';
import { siteService, type SiteRow } from '../../services/siteService';
import {
  sanitizeEmail,
  sanitizeMultilineText,
  sanitizeText,
} from '../../utils/sanitize';
import {
  MINUTES_PER_DAY,
  formatCoverageWindow,
  formatTimeOptionLabel,
  getCoverageDurationMinutes,
  getSiteCoverageWindow,
  resolveSiteTimeZone,
} from '../../utils/siteScheduling';
import { parseGeometryPoint } from '../../utils/geo';
import {
  DEFAULT_SITE_GEOFENCE_RADIUS_M,
  DEFAULT_WELFARE_NIGHT_END_MINUTE,
  DEFAULT_WELFARE_NIGHT_START_MINUTE,
} from './siteGeofence';
import { getCountryName } from './countries';
import ClientModal, { type ClientFormValues } from './ClientModal';
import ClientPortalSettingsModal from './ClientPortalSettingsModal';
import PostOrdersModal from './PostOrdersModal';
import SiteBuilder, { type SiteBuilderStep } from './SiteBuilder';
import SiteClientFilter from './SiteClientFilter';
import {
  UNASSIGNED_CLIENT_KEY,
  buildNewSiteClientOptions,
  buildSiteClientFilterOptions,
} from './siteClientOptions';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import OptionPicker from '../../components/OptionPicker';
import { useDismiss } from '../../hooks/useDismiss';
import { usePopover } from '../../hooks/usePopover';
import CreatePatrolPage from '../patrols/CreatePatrolPage';
import CreatePostsPage from '../patrols/CreatePostsPage';
import './SitesPage.css';

type MainTab = 'clients' | 'sites' | 'patrols';
type PatrolSubTab = 'patrols' | 'posts';
type ClientLifecycleAction = 'archive' | 'restore' | 'delete';

interface GeofenceEditTarget {
  siteId: string;
  clientContractId?: string | null;
  nonce?: number;
}

/** Sent by the shell when another area asks the site board to open on one
 *  client. The nonce re-runs the effect when the same client is picked twice. */
export interface SiteBoardClientTarget {
  clientContractId: string;
  nonce?: number;
}

interface SitesPageProps {
  geofenceEditTarget?: GeofenceEditTarget | null;
  defaultTab?: MainTab;
  showClientManagement?: boolean;
  showTabNavigation?: boolean;
  onReplayPatrolTour?: () => void;
  onReplayPostTour?: () => void;
  /** Client contracts are admin-only and live in Admin > Clients, so the board
   *  hands over rather than duplicating them. Absent for anyone who cannot go. */
  onManageClients?: () => void;
  clientFilterTarget?: SiteBoardClientTarget | null;
  /** The reverse handoff: the client board sends the shell to Sites, filtered.
   *  Only needed where this page renders without its own tab bar. */
  onOpenSites?: (clientContractId: string) => void;
}

interface ClientLifecycleTarget {
  action: ClientLifecycleAction;
  contract: ClientContractRow;
}

type ClientModalState =
  | { mode: 'create' }
  | { mode: 'edit'; contract: ClientContractRow };

const getSiteWelfareSummary = (site: SiteRow): string => {
  const mode = site.welfare_check_mode ?? 'automatic';
  if (mode === 'always') return t('Always on');
  if (mode === 'off') return t('Off by default');

  const start = site.welfare_night_start_minute ?? DEFAULT_WELFARE_NIGHT_START_MINUTE;
  const end = site.welfare_night_end_minute ?? DEFAULT_WELFARE_NIGHT_END_MINUTE;
  return t('Automatic (alone or {start}-{end})', {
    start: formatTimeOptionLabel(start),
    end: formatTimeOptionLabel(end),
  });
};

const getPolygonVertexCount = (site: SiteRow): number => {
  const ring = site.geofence_polygon?.coordinates?.[0];
  if (!ring?.length) return 0;

  const first = ring[0];
  const last = ring[ring.length - 1];
  const isClosed = first?.[0] === last?.[0] && first?.[1] === last?.[1];
  return isClosed ? ring.length - 1 : ring.length;
};

const getSiteGeofenceSummary = (site: SiteRow): string => {
  if (site.geofence_type === 'polygon') {
    const vertexCount = getPolygonVertexCount(site);
    return vertexCount > 0
      ? tn('Site geofence bounds ({count} point)', 'Site geofence bounds ({count} points)', vertexCount)
      : t('Site geofence bounds');
  }

  return t('Radius {radius} m', {
    radius: site.geofence_radius_m ?? site.attendance_radius_m ?? DEFAULT_SITE_GEOFENCE_RADIUS_M,
  });
};

type SiteStatusFilter = 'all' | 'round-clock' | 'fixed' | 'attention';
type ClientStatusFilter = 'all' | 'active' | 'pending' | 'attention';

interface SiteGroup {
  key: string;
  contract: ClientContractRow | null;
  sites: SiteRow[];
}

interface CoverageSegment {
  left: number;
  width: number;
}

const isRoundTheClockSite = (site: SiteRow): boolean => (
  getCoverageDurationMinutes(getSiteCoverageWindow(site)) >= MINUTES_PER_DAY
);

/** The cover window is drawn on a fixed 24-hour track, so a window that runs
 *  past midnight arrives as two segments: tonight's tail and tomorrow's head. */
const getCoverageSegments = (site: SiteRow): CoverageSegment[] => {
  if (isRoundTheClockSite(site)) return [{ left: 0, width: 100 }];

  const { startMinute, endMinute } = getSiteCoverageWindow(site);
  const toPercent = (minute: number) => (minute / MINUTES_PER_DAY) * 100;

  if (endMinute <= MINUTES_PER_DAY) {
    return [{ left: toPercent(startMinute), width: toPercent(endMinute - startMinute) }];
  }

  return [
    { left: toPercent(startMinute), width: toPercent(MINUTES_PER_DAY - startMinute) },
    { left: 0, width: toPercent(endMinute - MINUTES_PER_DAY) },
  ];
};

/** Setup gaps that stop a site working in the field: without a pin or a closed
 *  boundary the guard app cannot decide whether anyone is actually on site. */
const getSiteAttention = (site: SiteRow): string[] => {
  const flags: string[] = [];

  if (site.geofence_type === 'polygon') {
    if (getPolygonVertexCount(site) < 3) flags.push(t('Boundary unfinished'));
  } else if (!parseGeometryPoint(site.location)) {
    flags.push(t('No map pin'));
  }

  if (!site.address?.trim()) flags.push(t('No address'));

  return flags;
};

const getInviteStatusLabel = (contract: ClientContractRow): string => {
  const invite = contract.latest_invite;
  if (contract.status === 'archived') return t('Archived');
  if (contract.status === 'active') return t('Active');
  if (!invite) return t('Draft');
  if (invite.status === 'pending') return t('Invite pending');
  if (invite.status === 'accepted') return t('Active');
  if (invite.status === 'expired') return t('Invite expired');
  if (invite.status === 'cancelled') return t('Invite cancelled');
  return t('Draft');
};

/** What the client is still waiting on. The card leads with this because it is
 *  the only thing on it that changes what an admin should do next. */
const getClientAttention = (contract: ClientContractRow): string[] => {
  if (contract.status === 'archived') return [];

  const flags: string[] = [];
  if (!contract.contact_email?.trim()) flags.push(t('No invite email'));
  if (contract.site_count === 0) flags.push(t('No sites yet'));
  return flags;
};

/** An expired invite is a to-do too. It earns no chip because the status pill
 *  beside the name already says it, but it still counts as unfinished setup. */
const needsClientSetup = (contract: ClientContractRow): boolean => (
  contract.status !== 'archived'
  && (getClientAttention(contract).length > 0 || contract.latest_invite?.status === 'expired')
);

const isClientPortalActive = (contract: ClientContractRow): boolean => (
  contract.status !== 'archived'
  && (contract.status === 'active' || contract.latest_invite?.status === 'accepted')
);

const isClientInvitePending = (contract: ClientContractRow): boolean => (
  contract.status !== 'archived' && contract.latest_invite?.status === 'pending'
);

const getClientStatusTone = (contract: ClientContractRow): string => {
  if (contract.status === 'archived') return 'archived';
  if (isClientPortalActive(contract)) return 'active';
  if (isClientInvitePending(contract)) return 'pending';
  const inviteStatus = contract.latest_invite?.status;
  if (inviteStatus === 'expired' || inviteStatus === 'cancelled') return 'stalled';
  return 'draft';
};

const formatInviteExpiry = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString(getLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
};

interface ClientLifecycleModalProps {
  target: ClientLifecycleTarget;
  isSubmitting: boolean;
  pendingAction: ClientLifecycleAction | null;
  error: string;
  onConfirm: () => void;
  onArchiveInstead?: () => void;
  onCancel: () => void;
}

function ClientLifecycleModal({
  target,
  isSubmitting,
  pendingAction,
  error,
  onConfirm,
  onArchiveInstead,
  onCancel,
}: ClientLifecycleModalProps) {
  const { action, contract } = target;
  const isDelete = action === 'delete';
  const title = isDelete
    ? t('Delete client permanently')
    : action === 'archive'
      ? t('Archive client')
      : t('Restore client');
  const body = isDelete
    ? t('Deleting {name} will permanently remove the client contract, portal access, invites, linked sites, schedules, patrols, posts, reports, alarms, shift history, and all associated operational data. This cannot be undone.', { name: contract.name })
    : action === 'archive'
      ? t('Archiving {name} hides the client and its linked sites and data from standard views and creation screens. It can be restored later.', { name: contract.name })
      : t('Restoring {name} makes the client and its linked sites available in standard views and creation screens again.', { name: contract.name });
  const confirmLabel = isSubmitting
    ? pendingAction === 'delete' ? t('Deleting...') : pendingAction === 'archive' ? t('Archiving...') : t('Restoring...')
    : action === 'delete' ? t('Delete Client') : action === 'archive' ? t('Archive Client') : t('Restore Client');

  return (
    <div className="modal-overlay" onClick={(event) => event.target === event.currentTarget && !isSubmitting && onCancel()}>
      <div className="modal modal--sm client-lifecycle-modal">
        <div className="modal__header">
          <div>
            <div className="sites-flow__eyebrow">{t('Client lifecycle')}</div>
            <h2 className="modal__title">{title}</h2>
          </div>
        </div>
        <div className={`client-lifecycle-modal__notice${isDelete ? ' client-lifecycle-modal__notice--danger' : ''}`}>
          {body}
        </div>
        {error ? <div className="msg--error">{error}</div> : null}
        <div className="client-lifecycle-modal__actions">
          <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isSubmitting}>
            {t('Cancel')}
          </button>
          {isDelete && onArchiveInstead && (
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onArchiveInstead}
              disabled={isSubmitting}
            >
              {pendingAction === 'archive' ? t('Archiving...') : t('Archive instead')}
            </button>
          )}
          <button
            type="button"
            className={`btn ${isDelete ? 'btn--danger' : 'btn--primary'}`}
            onClick={onConfirm}
            disabled={isSubmitting}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SitesPage({
  geofenceEditTarget = null,
  defaultTab,
  showClientManagement = true,
  showTabNavigation = true,
  onReplayPatrolTour,
  onReplayPostTour,
  onManageClients,
  clientFilterTarget = null,
  onOpenSites,
}: SitesPageProps) {
  useI18n();
  // Tab navigation
  const [tab, setTab] = useState<MainTab>(defaultTab ?? (showClientManagement ? 'clients' : 'sites'));
  const [patrolSubTab, setPatrolSubTab] = useState<PatrolSubTab>('patrols');
  const [patrolBuilderOpen, setPatrolBuilderOpen] = useState(false);
  const [postBuilderOpen, setPostBuilderOpen] = useState(false);

  // Client contracts + sites
  const [contracts, setContracts] = useState<ClientContractRow[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [sites, setSites] = useState<SiteRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clientError, setClientError] = useState('');
  const [clientSuccess, setClientSuccess] = useState('');
  const [clientModal, setClientModal] = useState<ClientModalState | null>(null);
  const [isSubmittingClient, setIsSubmittingClient] = useState(false);
  const [inviteActionId, setInviteActionId] = useState<string | null>(null);
  const [showArchivedClients, setShowArchivedClients] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState<ClientStatusFilter>('all');
  const [openClientMenuId, setOpenClientMenuId] = useState<string | null>(null);
  const [portalSettingsContract, setPortalSettingsContract] = useState<ClientContractRow | null>(null);
  const [companyCountryCode, setCompanyCountryCode] = useState<string | null>(null);
  const [postOrdersSite, setPostOrdersSite] = useState<{ id: string; name: string } | null>(null);

  // Site board filters -- the sites tab shows every site by default and
  // narrows from the toolbar, so the client selection above is not a filter.
  const [siteSearch, setSiteSearch] = useState('');
  const [siteClientFilter, setSiteClientFilter] = useState<string | null>(null);
  const [siteStatusFilter, setSiteStatusFilter] = useState<SiteStatusFilter>('all');
  const newSitePicker = usePopover();
  const [clientLifecycleTarget, setClientLifecycleTarget] = useState<ClientLifecycleTarget | null>(null);
  const [isSubmittingClientLifecycle, setIsSubmittingClientLifecycle] = useState(false);
  const [clientLifecyclePendingAction, setClientLifecyclePendingAction] = useState<ClientLifecycleAction | null>(null);
  const [clientLifecycleError, setClientLifecycleError] = useState('');

  // Site builder -- one workspace for creating and for editing a site
  const [siteBuilderOpen, setSiteBuilderOpen] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState<string | null>(null);
  const [builderInitialStep, setBuilderInitialStep] = useState<SiteBuilderStep>('location');

  // Delete confirmation
  const [deleteConfirmSiteId, setDeleteConfirmSiteId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const consumedGeofenceEditTargetRef = useRef<string | null>(null);
  const consumedClientFilterTargetRef = useRef<string | null>(null);
  const clientMenuRef = useRef<HTMLDivElement | null>(null);

  useDismiss(clientMenuRef, openClientMenuId !== null, () => setOpenClientMenuId(null));

  useEffect(() => {
    if (!showClientManagement && tab === 'clients') {
      setTab('sites');
    }
  }, [showClientManagement, tab]);

  const selectedClient = useMemo(
    () => contracts.find((contract) => contract.id === selectedClientId) ?? null,
    [contracts, selectedClientId],
  );

  /** The client board is a directory, like the site board beside it: every
   *  client is on screen and the signals and search narrow the list. */
  const boardClients = useMemo(() => {
    const query = clientSearch.trim().toLowerCase();
    return contracts.filter((contract) => {
      if (clientStatusFilter === 'active' && !isClientPortalActive(contract)) return false;
      if (clientStatusFilter === 'pending' && !isClientInvitePending(contract)) return false;
      if (clientStatusFilter === 'attention' && !needsClientSetup(contract)) return false;
      if (!query) return true;
      return [
        contract.name,
        contract.contact_name,
        contract.contact_email,
        contract.billing_email,
      ].some((value) => value?.toLowerCase().includes(query));
    });
  }, [clientSearch, clientStatusFilter, contracts]);

  const portalActiveCount = useMemo(
    () => contracts.filter(isClientPortalActive).length,
    [contracts],
  );

  const invitePendingCount = useMemo(
    () => contracts.filter(isClientInvitePending).length,
    [contracts],
  );

  const clientAttentionCount = useMemo(
    () => contracts.filter(needsClientSetup).length,
    [contracts],
  );

  const hasClientFilters = clientStatusFilter !== 'all'
    || showArchivedClients
    || clientSearch.trim() !== '';

  const editingSite = useMemo(
    () => sites.find((site) => site.id === editingSiteId) ?? null,
    [editingSiteId, sites],
  );

  const linkedSiteCount = useMemo(
    () => sites.filter((site) => Boolean(site.client_contract_id)).length,
    [sites],
  );

  const siteCountByClient = useMemo(() => {
    const counts = new Map<string, number>();
    sites.forEach((site) => {
      const key = site.client_contract_id ?? UNASSIGNED_CLIENT_KEY;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [sites]);

  const clientsWithSitesCount = useMemo(
    () => contracts.filter((contract) => (siteCountByClient.get(contract.id) ?? 0) > 0).length,
    [contracts, siteCountByClient],
  );

  const roundTheClockCount = useMemo(() => sites.filter(isRoundTheClockSite).length, [sites]);

  const attentionCount = useMemo(
    () => sites.filter((site) => getSiteAttention(site).length > 0).length,
    [sites],
  );

  const assignableClients = useMemo(
    () => contracts.filter((contract) => contract.status !== 'archived'),
    [contracts],
  );

  // Not memoised: the labels come from t(), which follows the live language.
  const siteClientFilterOptions = buildSiteClientFilterOptions(
    contracts,
    siteCountByClient,
    siteClientFilter,
  );
  const newSiteClientOptions = buildNewSiteClientOptions(assignableClients, siteCountByClient);

  const clientsWithoutSites = useMemo(
    () => contracts.filter((contract) => (
      contract.status !== 'archived' && (siteCountByClient.get(contract.id) ?? 0) === 0
    )),
    [contracts, siteCountByClient],
  );

  const boardGroups = useMemo<SiteGroup[]>(() => {
    const query = siteSearch.trim().toLowerCase();
    const matches = sites.filter((site) => {
      const clientKey = site.client_contract_id ?? UNASSIGNED_CLIENT_KEY;
      if (siteClientFilter !== null && clientKey !== siteClientFilter) return false;
      if (siteStatusFilter === 'round-clock' && !isRoundTheClockSite(site)) return false;
      if (siteStatusFilter === 'fixed' && isRoundTheClockSite(site)) return false;
      if (siteStatusFilter === 'attention' && getSiteAttention(site).length === 0) return false;
      if (!query) return true;
      return [site.name, site.address, site.client_contract?.name]
        .some((value) => value?.toLowerCase().includes(query));
    });

    const byClient = new Map<string, SiteRow[]>();
    matches.forEach((site) => {
      const key = site.client_contract_id ?? UNASSIGNED_CLIENT_KEY;
      const bucket = byClient.get(key);
      if (bucket) bucket.push(site);
      else byClient.set(key, [site]);
    });

    const groups: SiteGroup[] = contracts
      .filter((contract) => byClient.has(contract.id))
      .map((contract) => ({ key: contract.id, contract, sites: byClient.get(contract.id) ?? [] }));

    const orphans = byClient.get(UNASSIGNED_CLIENT_KEY);
    if (orphans?.length) groups.push({ key: UNASSIGNED_CLIENT_KEY, contract: null, sites: orphans });

    return groups;
  }, [contracts, siteClientFilter, siteSearch, siteStatusFilter, sites]);

  const boardFilterClient = useMemo(
    () => (siteClientFilter !== null && siteClientFilter !== UNASSIGNED_CLIENT_KEY
      ? contracts.find((contract) => contract.id === siteClientFilter) ?? null
      : null),
    [contracts, siteClientFilter],
  );

  const boardClientIsArchived = boardFilterClient?.status === 'archived';
  const boardNeedsClientChoice = !boardFilterClient && assignableClients.length > 1;
  const hasBoardFilters = siteClientFilter !== null
    || siteStatusFilter !== 'all'
    || siteSearch.trim() !== '';

  const loadSites = useCallback(async () => {
    setIsLoading(true);
    try {
      const [contractRows, siteRows] = await Promise.all([
        clientService.fetchClientContracts({ includeArchived: showArchivedClients }),
        siteService.fetchAllSites(null, { includeArchivedClients: showArchivedClients }),
      ]);
      setContracts(contractRows);
      setSites(siteRows);
      setSelectedClientId((current) => (
        current && contractRows.some((contract) => contract.id === current)
          ? current
          : contractRows[0]?.id ?? null
      ));
      // A deleted client cannot stay the board filter: the trigger would have
      // nothing to name and the board would sit empty with no obvious cause.
      setSiteClientFilter((current) => (
        current === null
          || current === UNASSIGNED_CLIENT_KEY
          || contractRows.some((contract) => contract.id === current)
          ? current
          : null
      ));
    } catch (err) {
      console.error('Error loading client setup:', (err as Error).message);
      setClientError((err as Error).message || t('Failed to load clients'));
    } finally {
      setIsLoading(false);
    }
  }, [showArchivedClients]);

  useEffect(() => { void loadSites(); }, [loadSites]);

  // Only a default for the new-client form; the database applies the same
  // default when the form sends none, so a failure here costs nothing.
  useEffect(() => {
    let cancelled = false;
    clientService.fetchCompanyCountryCode()
      .then((code) => { if (!cancelled) setCompanyCountryCode(code); })
      .catch((err: Error) => console.error('Error loading company country:', err.message));
    return () => { cancelled = true; };
  }, []);

  const handleConfirmDelete = async () => {
    if (!deleteConfirmSiteId) return;
    setIsDeleting(true);
    try {
      await siteService.deleteSite(deleteConfirmSiteId);
      setDeleteConfirmSiteId(null);
      loadSites();
    } catch (err) {
      console.error('Error deleting site:', (err as Error).message);
      alert('Failed to delete site: ' + (err as Error).message);
    } finally {
      setIsDeleting(false);
    }
  };

  const beginSiteEdit = useCallback((site: SiteRow, step: SiteBuilderStep = 'location') => {
    setTab('sites');
    setSelectedClientId(site.client_contract_id ?? null);
    setClientError('');
    setEditingSiteId(site.id);
    setBuilderInitialStep(step);
    setSiteBuilderOpen(true);
  }, []);

  useEffect(() => {
    if (!geofenceEditTarget?.siteId) return;

    const targetKey = `${geofenceEditTarget.siteId}:${geofenceEditTarget.nonce ?? 'static'}`;
    if (consumedGeofenceEditTargetRef.current === targetKey) return;

    setTab('sites');
    if (geofenceEditTarget.clientContractId) {
      setSelectedClientId(geofenceEditTarget.clientContractId);
    }

    const targetSite = sites.find((site) => site.id === geofenceEditTarget.siteId);
    if (!targetSite) return;

    consumedGeofenceEditTargetRef.current = targetKey;
    beginSiteEdit(targetSite, 'boundary');
  }, [
    beginSiteEdit,
    geofenceEditTarget?.clientContractId,
    geofenceEditTarget?.nonce,
    geofenceEditTarget?.siteId,
    sites,
  ]);

  useEffect(() => {
    if (!clientFilterTarget?.clientContractId) return;

    const targetKey = `${clientFilterTarget.clientContractId}:${clientFilterTarget.nonce ?? 'static'}`;
    if (consumedClientFilterTargetRef.current === targetKey) return;
    consumedClientFilterTargetRef.current = targetKey;

    setTab('sites');
    setSelectedClientId(clientFilterTarget.clientContractId);
    setSiteClientFilter(clientFilterTarget.clientContractId);
    setSiteStatusFilter('all');
    setSiteSearch('');
  }, [clientFilterTarget?.clientContractId, clientFilterTarget?.nonce]);

  const openClientModal = (state: ClientModalState) => {
    setClientError('');
    setClientSuccess('');
    setClientModal(state);
  };

  const closeClientModal = () => {
    if (isSubmittingClient) return;
    setClientModal(null);
    setClientError('');
  };

  const handleSubmitClient = async (values: ClientFormValues) => {
    if (!clientModal) return;

    const params = {
      name: sanitizeText(values.clientName, 120),
      countryCode: values.countryCode || null,
      billingEmail: sanitizeEmail(values.billingEmail) || null,
      contactName: sanitizeText(values.contactName, 120) || null,
      contactEmail: sanitizeEmail(values.contactEmail) || null,
      contactPhone: sanitizeText(values.contactPhone, 40) || null,
      notes: sanitizeMultilineText(values.clientNotes, 1000) || null,
    };

    if (!params.name) {
      setClientError(t('Client name is required'));
      return;
    }

    setIsSubmittingClient(true);
    setClientError('');
    setClientSuccess('');

    try {
      if (clientModal.mode === 'edit') {
        const updated = await clientService.updateClientContract(clientModal.contract.id, params);
        setContracts((current) => current.map((contract) => (
          contract.id === updated.id ? { ...contract, ...updated } : contract
        )));
        setClientModal(null);
        setClientSuccess(t('{name} has been updated.', { name: updated.name }));
      } else {
        const created = await clientService.createClientContract(params);
        setClientModal(null);
        setSelectedClientId(created.id);
        setClientSuccess(t('Created {name}. Add sites, then send the client invite when ready.', { name: created.name }));
        await loadSites();
      }
    } catch (err) {
      const fallback = clientModal.mode === 'edit'
        ? t('Failed to update client')
        : t('Failed to create client');
      setClientError((err as Error).message || fallback);
    } finally {
      setIsSubmittingClient(false);
    }
  };

  const handleSendInvite = async (contract: ClientContractRow) => {
    if (contract.status === 'archived') {
      setClientError(t('Restore this client before sending a portal invite'));
      return;
    }
    setInviteActionId(contract.id);
    setClientError('');
    setClientSuccess('');

    try {
      const invite = await clientService.sendClientInvite(
        contract.id,
        contract.contact_email,
        contract.contact_name,
      );
      setClientSuccess(t('Invite sent to {email}.', { email: invite.email }));
      await loadSites();
    } catch (err) {
      setClientError((err as Error).message || t('Failed to send invite'));
    } finally {
      setInviteActionId(null);
    }
  };

  const handleResendInvite = async (contract: ClientContractRow) => {
    if (!contract.latest_invite) return;
    if (contract.status === 'archived') {
      setClientError(t('Restore this client before resending a portal invite'));
      return;
    }
    setInviteActionId(contract.latest_invite.id);
    setClientError('');
    setClientSuccess('');

    try {
      const invite = await clientService.resendClientInvite(contract.latest_invite.id);
      setClientSuccess(t('Invite resent to {email}.', { email: invite.email }));
      await loadSites();
    } catch (err) {
      setClientError((err as Error).message || t('Failed to resend invite'));
    } finally {
      setInviteActionId(null);
    }
  };

  const handleCancelInvite = async (contract: ClientContractRow) => {
    if (!contract.latest_invite) return;
    setInviteActionId(contract.latest_invite.id);
    setClientError('');
    setClientSuccess('');

    try {
      await clientService.cancelClientInvite(contract.latest_invite.id);
      setClientSuccess(t('The pending invite has been cancelled.'));
      await loadSites();
    } catch (err) {
      setClientError((err as Error).message || t('Failed to cancel invite'));
    } finally {
      setInviteActionId(null);
    }
  };

  const openClientLifecycleModal = (action: ClientLifecycleAction, contract: ClientContractRow) => {
    setClientLifecycleError('');
    setClientError('');
    setClientSuccess('');
    setClientLifecycleTarget({ action, contract });
  };

  const handleConfirmClientLifecycle = async () => {
    if (!clientLifecycleTarget) return;

    const { action, contract } = clientLifecycleTarget;
    setIsSubmittingClientLifecycle(true);
    setClientLifecyclePendingAction(action);
    setClientLifecycleError('');
    setClientError('');
    setClientSuccess('');

    try {
      if (action === 'archive') {
        await clientService.archiveClientContract(contract.id);
        setClientSuccess(t('{name} has been archived.', { name: contract.name }));
      } else if (action === 'restore') {
        await clientService.restoreClientContract(contract.id);
        setClientSuccess(t('{name} has been restored.', { name: contract.name }));
      } else {
        const result = await clientService.deleteClientContract(contract.id);
        const deletedSiteCount = result.deleted_site_count ?? contract.site_count;
        setClientSuccess(deletedSiteCount
          ? tn(
              '{name} has been permanently deleted with {count} linked site.',
              '{name} has been permanently deleted with {count} linked sites.',
              deletedSiteCount,
              { name: contract.name },
            )
          : t('{name} has been permanently deleted.', { name: contract.name }));
      }

      setClientLifecycleTarget(null);
      await loadSites();
    } catch (err) {
      const fallback = action === 'archive'
        ? t('Failed to archive client')
        : action === 'restore'
          ? t('Failed to restore client')
          : t('Failed to delete client');
      setClientLifecycleError((err as Error).message || fallback);
    } finally {
      setIsSubmittingClientLifecycle(false);
      setClientLifecyclePendingAction(null);
    }
  };

  const handleArchiveInsteadFromDelete = async () => {
    if (!clientLifecycleTarget || clientLifecycleTarget.action !== 'delete') return;

    const { contract } = clientLifecycleTarget;
    setIsSubmittingClientLifecycle(true);
    setClientLifecyclePendingAction('archive');
    setClientLifecycleError('');
    setClientError('');
    setClientSuccess('');

    try {
      await clientService.archiveClientContract(contract.id);
      setClientSuccess(t('{name} has been archived.', { name: contract.name }));
      setClientLifecycleTarget(null);
      await loadSites();
    } catch (err) {
      setClientLifecycleError((err as Error).message || t('Failed to archive client'));
    } finally {
      setIsSubmittingClientLifecycle(false);
      setClientLifecyclePendingAction(null);
    }
  };

  const handleCloseSiteBuilder = () => {
    setSiteBuilderOpen(false);
    setEditingSiteId(null);
  };

  const openSiteBuilderForClient = useCallback((contract: ClientContractRow) => {
    setEditingSiteId(null);
    setBuilderInitialStep('location');
    setClientError('');
    setSelectedClientId(contract.id);
    // Admin > Clients renders this page without a tab bar, so switching tabs
    // there would strand the user on the site board with no way back.
    if (showTabNavigation) setTab('sites');
    setSiteBuilderOpen(true);
  }, [showTabNavigation]);

  /** Sites for one client live on the site board, not here -- either on the
   *  neighbouring tab or, from Admin > Clients, over in the Sites area. */
  const openSitesForClient = (contract: ClientContractRow) => {
    setSelectedClientId(contract.id);
    setSiteClientFilter(contract.id);
    setSiteStatusFilter('all');
    setSiteSearch('');
    if (showTabNavigation) setTab('sites');
    else onOpenSites?.(contract.id);
  };

  const clearClientFilters = () => {
    setClientStatusFilter('all');
    setClientSearch('');
    setShowArchivedClients(false);
  };

  /** A new site always belongs to a client, so the board asks for one only when
   *  the current filter has not already picked it. */
  const handleCreateSiteFromBoard = () => {
    if (boardFilterClient) {
      openSiteBuilderForClient(boardFilterClient);
      return;
    }
    if (assignableClients.length === 1) {
      openSiteBuilderForClient(assignableClients[0]);
      return;
    }
    if (assignableClients.length === 0) {
      setClientError(t('Create or select a client before adding a site'));
      return;
    }
    newSitePicker.toggle();
  };

  const handleNewSiteClientChosen = (clientId: string) => {
    // The builder takes focus itself, so none is handed back to the button.
    newSitePicker.close({ restoreFocus: false });
    const contract = assignableClients.find((candidate) => candidate.id === clientId);
    if (contract) openSiteBuilderForClient(contract);
  };

  const clearBoardFilters = () => {
    setSiteClientFilter(null);
    setSiteStatusFilter('all');
    setSiteSearch('');
  };

  const openPatrolsForSite = (site: SiteRow) => {
    setSelectedClientId(site.client_contract_id ?? null);
    setPatrolSubTab('patrols');
    setPatrolBuilderOpen(true);
    setTab('patrols');
  };

  const handleSiteSaved = (savedName: string) => {
    const wasEditing = editingSiteId != null;
    handleCloseSiteBuilder();
    setClientError('');
    setClientSuccess(wasEditing
      ? t('Updated {name}.', { name: savedName })
      : t('Created {name}.', { name: savedName }));
    void loadSites();
  };

  const handleEditPatrolSiteGeofence = useCallback((target: { siteId: string; clientContractId?: string | null }) => {
    setTab('sites');
    if (target.clientContractId) {
      setSelectedClientId(target.clientContractId);
    }

    const targetSite = sites.find((site) => site.id === target.siteId);
    if (targetSite) {
      beginSiteEdit(targetSite, 'boundary');
    }
  }, [beginSiteEdit, sites]);

  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  return (
    <div>
      {showTabNavigation && (
        <div className="sites__tabs">
        {showClientManagement && (
          <button
            type="button"
            className={`sites__tab${tab === 'clients' ? ' sites__tab--active' : ''}`}
            onClick={() => setTab('clients')}
          >
            {t('Clients')}
          </button>
        )}
        <button
          type="button"
          className={`sites__tab${tab === 'sites' ? ' sites__tab--active' : ''}`}
          onClick={() => setTab('sites')}
        >
          {t('Sites')}
        </button>
        <button
          type="button"
          className={`sites__tab${tab === 'patrols' ? ' sites__tab--active' : ''}`}
          onClick={() => setTab('patrols')}
        >
          {t('Patrols & Posts')}
        </button>
      </div>
      )}

      {tab === 'patrols' && (
        <div>
          <div className="sites__subtabs">
            <button
              type="button"
              className={`sites__subtab${patrolSubTab === 'patrols' ? ' sites__subtab--active' : ''}`}
              onClick={() => { setPatrolSubTab('patrols'); setPatrolBuilderOpen(false); setPostBuilderOpen(false); }}
            >
              {t('Patrols')}
            </button>
            <button
              type="button"
              className={`sites__subtab${patrolSubTab === 'posts' ? ' sites__subtab--active' : ''}`}
              onClick={() => { setPatrolSubTab('posts'); setPatrolBuilderOpen(false); setPostBuilderOpen(false); }}
            >
              {t('Posts')}
            </button>
          </div>

          {patrolSubTab === 'patrols' && !patrolBuilderOpen && (
            <div className="sites__patrol-landing">
              <div className="sites__patrol-landing-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12h18M3 6h18M3 18h12" /><circle cx="19" cy="18" r="3" /><path d="M19 16v2l1 1" /></svg>
              </div>
              <h2 className="sites__patrol-landing-title">{t('Patrol Builder')}</h2>
              <p className="sites__patrol-landing-copy">
                {t('Build and manage patrol routes, checkpoints, and QR verification for any site.')}
              </p>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setPatrolBuilderOpen(true)}
              >
                {t('Open Patrol Builder')}
              </button>
            </div>
          )}

          {patrolSubTab === 'patrols' && patrolBuilderOpen && (
            <CreatePatrolPage
              clientContractId={selectedClientId}
              clientName={selectedClient?.name ?? null}
              onEditSiteGeofence={handleEditPatrolSiteGeofence}
              onExit={() => setPatrolBuilderOpen(false)}
              onReplayTour={onReplayPatrolTour}
            />
          )}

          {patrolSubTab === 'posts' && !postBuilderOpen && (
            <div className="sites__patrol-landing">
              <div className="sites__patrol-landing-icon">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a7 7 0 017 7c0 5-7 13-7 13S5 14 5 9a7 7 0 017-7z" /><circle cx="12" cy="9" r="2.5" /></svg>
              </div>
              <h2 className="sites__patrol-landing-title">{t('Post Builder')}</h2>
              <p className="sites__patrol-landing-copy">
                {t('Place and manage fixed guard posts and their geofences for any site.')}
              </p>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => setPostBuilderOpen(true)}
              >
                {t('Open Post Builder')}
              </button>
            </div>
          )}

          {patrolSubTab === 'posts' && postBuilderOpen && (
            <CreatePostsPage
              clientContractId={selectedClientId}
              clientName={selectedClient?.name ?? null}
              onExit={() => setPostBuilderOpen(false)}
              onReplayTour={onReplayPostTour}
            />
          )}
        </div>
      )}

      {tab === 'clients' && (
      <div className="page client-board">
        <div className="page__header">
          <div>
            <h1 className="page__title">{t('Clients')}</h1>
            <p className="client-board__lead">
              {t('Every client under contract, what each is waiting on, and the next step.')}
            </p>
          </div>
          <div className="sites__header-actions">
            <button type="button" className="btn btn--secondary" onClick={() => { void loadSites(); }}>
              <FiRefreshCw aria-hidden="true" />
              {t('Refresh')}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openClientModal({ mode: 'create' })}
            >
              <FiPlus aria-hidden="true" />
              {t('New client')}
            </button>
          </div>
        </div>

        <div className="client-board__banners" aria-live="polite">
          {clientError && !clientModal && <div className="msg--error">{clientError}</div>}
          {clientSuccess && <div className="msg--success">{clientSuccess}</div>}
        </div>

        {contracts.length > 0 && (
        <div className="client-board__signals">
          <button
            type="button"
            className="client-board__signal"
            aria-pressed={clientStatusFilter === 'all'}
            onClick={() => setClientStatusFilter('all')}
          >
            <span className="client-board__signal-label"><FiUsers aria-hidden="true" />{t('Clients')}</span>
            <span className="client-board__signal-value">{contracts.length}</span>
            <span className="client-board__signal-note">
              {tn('{count} site under contract', '{count} sites under contract', linkedSiteCount)}
            </span>
          </button>
          <button
            type="button"
            className="client-board__signal"
            aria-pressed={clientStatusFilter === 'active'}
            onClick={() => setClientStatusFilter(clientStatusFilter === 'active' ? 'all' : 'active')}
          >
            <span className="client-board__signal-label"><FiCheckCircle aria-hidden="true" />{t('Portal live')}</span>
            <span className="client-board__signal-value">{portalActiveCount}</span>
            <span className="client-board__signal-note">{t('Client can sign in and watch their sites')}</span>
          </button>
          <button
            type="button"
            className="client-board__signal"
            aria-pressed={clientStatusFilter === 'pending'}
            onClick={() => setClientStatusFilter(clientStatusFilter === 'pending' ? 'all' : 'pending')}
          >
            <span className="client-board__signal-label"><FiSend aria-hidden="true" />{t('Invite pending')}</span>
            <span className="client-board__signal-value">{invitePendingCount}</span>
            <span className="client-board__signal-note">{t('Sent, waiting on the client')}</span>
          </button>
          <button
            type="button"
            className={`client-board__signal client-board__signal--attention${clientAttentionCount > 0 ? ' client-board__signal--flagged' : ''}`}
            aria-pressed={clientStatusFilter === 'attention'}
            onClick={() => setClientStatusFilter(clientStatusFilter === 'attention' ? 'all' : 'attention')}
          >
            <span className="client-board__signal-label"><FiAlertTriangle aria-hidden="true" />{t('Needs setup')}</span>
            <span className="client-board__signal-value">{clientAttentionCount}</span>
            <span className="client-board__signal-note">{t('Missing an invite email or a site')}</span>
          </button>
        </div>
        )}

        {contracts.length > 0 && (
        <div className="client-board__toolbar">
          <div className="client-board__search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              className="form__input"
              value={clientSearch}
              onChange={(event) => setClientSearch(event.target.value)}
              placeholder={t('Search clients, contacts or emails…')}
              aria-label={t('Search clients, contacts or emails…')}
              spellCheck={false}
            />
          </div>
          <button
            type="button"
            className="client-board__chip"
            aria-pressed={showArchivedClients}
            onClick={() => setShowArchivedClients((current) => !current)}
          >
            <FiArchive aria-hidden="true" />
            {t('Include archived')}
          </button>
          {hasClientFilters && (
            <button type="button" className="client-board__reset" onClick={clearClientFilters}>
              {t('Clear filters')}
            </button>
          )}
        </div>
        )}

        {contracts.length === 0 ? (
          <div className="client-board__empty">
            <div className="client-board__empty-icon"><FiUsers size={26} aria-hidden="true" /></div>
            <h2>{t('No clients yet')}</h2>
            <p>{t('A client contract sits at the top of the chain: sites hang off it and the portal invite goes to its contact. The name is all you need to start.')}</p>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openClientModal({ mode: 'create' })}
            >
              <FiPlus aria-hidden="true" />
              {t('New client')}
            </button>
          </div>
        ) : boardClients.length === 0 ? (
          <div className="client-board__empty">
            <div className="client-board__empty-icon"><FiSearch size={26} aria-hidden="true" /></div>
            <h2>{t('No clients match')}</h2>
            <p>{t('Nothing here matches the current search and filters.')}</p>
            <button type="button" className="btn btn--secondary" onClick={clearClientFilters}>
              {t('Clear filters')}
            </button>
          </div>
        ) : (
          <div className="client-board__grid">
            {boardClients.map((contract) => {
              const attention = getClientAttention(contract);
              const needsSetup = needsClientSetup(contract);
              const isArchived = contract.status === 'archived';
              const invite = contract.latest_invite;
              const invitePending = isClientInvitePending(contract);
              const portalActive = isClientPortalActive(contract);
              const inviteBusy = inviteActionId === contract.id
                || (invite ? inviteActionId === invite.id : false);
              const inviteExpiry = formatInviteExpiry(invite?.expires_at);
              const menuOpen = openClientMenuId === contract.id;

              return (
                <article
                  key={contract.id}
                  className={`client-card${isArchived ? ' client-card--archived' : ''}${needsSetup ? ' client-card--attention' : ''}`}
                >
                  <div className="client-card__head">
                    <div className="client-card__identity">
                      <h2 className="client-card__name">{contract.name}</h2>
                      <span className={`client-card__status client-card__status--${getClientStatusTone(contract)}`}>
                        {getInviteStatusLabel(contract)}
                      </span>
                    </div>
                    <div className="client-card__head-actions">
                      <button
                        type="button"
                        className="client-card__icon-btn"
                        aria-label={t('Edit {name}', { name: contract.name })}
                        onClick={() => openClientModal({ mode: 'edit', contract })}
                      >
                        <FiEdit2 aria-hidden="true" />
                      </button>
                      <div
                        className="client-card__more"
                        ref={menuOpen ? clientMenuRef : undefined}
                      >
                        <button
                          type="button"
                          className="client-card__icon-btn"
                          aria-label={t('More actions for {name}', { name: contract.name })}
                          aria-haspopup="menu"
                          aria-expanded={menuOpen}
                          onClick={() => setOpenClientMenuId(menuOpen ? null : contract.id)}
                        >
                          <FiMoreHorizontal aria-hidden="true" />
                        </button>
                        {menuOpen && (
                          <div className="client-card__menu" role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              className="client-card__menu-item"
                              onClick={() => {
                                setOpenClientMenuId(null);
                                setPortalSettingsContract(contract);
                              }}
                            >
                              {t('Portal visibility')}
                            </button>
                            {invitePending && (
                              <button
                                type="button"
                                role="menuitem"
                                className="client-card__menu-item"
                                disabled={inviteBusy}
                                onClick={() => {
                                  setOpenClientMenuId(null);
                                  void handleCancelInvite(contract);
                                }}
                              >
                                {t('Cancel invite')}
                              </button>
                            )}
                            {!isArchived && (
                              <button
                                type="button"
                                role="menuitem"
                                className="client-card__menu-item"
                                onClick={() => {
                                  setOpenClientMenuId(null);
                                  openClientLifecycleModal('archive', contract);
                                }}
                              >
                                {t('Archive client')}
                              </button>
                            )}
                            <button
                              type="button"
                              role="menuitem"
                              className="client-card__menu-item client-card__menu-item--danger"
                              onClick={() => {
                                setOpenClientMenuId(null);
                                openClientLifecycleModal('delete', contract);
                              }}
                            >
                              {t('Delete client')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {attention.length > 0 && (
                    <ul className="client-card__flags">
                      {attention.map((flag) => (
                        <li key={flag} className="client-card__flag">
                          <FiAlertTriangle aria-hidden="true" />
                          {flag}
                        </li>
                      ))}
                    </ul>
                  )}

                  <dl className="client-card__facts">
                    <div className="client-card__fact client-card__fact--wide">
                      <dt><FiMail aria-hidden="true" />{t('Invite email')}</dt>
                      <dd className="client-card__fact--email">
                        {contract.contact_email || <em>{t('Not set')}</em>}
                      </dd>
                    </div>
                    <div className="client-card__fact">
                      <dt><FiMapPin aria-hidden="true" />{t('Sites')}</dt>
                      <dd>{contract.site_count}</dd>
                    </div>
                    {contract.contact_name && (
                      <div className="client-card__fact">
                        <dt><FiUser aria-hidden="true" />{t('Contact')}</dt>
                        <dd>{contract.contact_name}</dd>
                      </div>
                    )}
                    {contract.contact_phone && (
                      <div className="client-card__fact">
                        <dt><FiPhone aria-hidden="true" />{t('Phone')}</dt>
                        <dd>{contract.contact_phone}</dd>
                      </div>
                    )}
                    {contract.billing_email && (
                      <div className="client-card__fact">
                        <dt><FiCreditCard aria-hidden="true" />{t('Billing')}</dt>
                        <dd className="client-card__fact--email">{contract.billing_email}</dd>
                      </div>
                    )}
                  </dl>

                  {invitePending && (
                    <p className="client-card__invite">
                      <FiClock aria-hidden="true" />
                      {inviteExpiry
                        ? t('Invite sent to {email}, expires {date}', { email: invite?.email ?? '', date: inviteExpiry })
                        : t('Invite sent to {email}', { email: invite?.email ?? '' })}
                    </p>
                  )}

                  {contract.notes && <p className="client-card__notes">{contract.notes}</p>}

                  <div className="client-card__actions">
                    {isArchived ? (
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={() => openClientLifecycleModal('restore', contract)}
                      >
                        {t('Restore client')}
                      </button>
                    ) : invitePending ? (
                      <button
                        type="button"
                        className="btn btn--secondary"
                        disabled={inviteBusy}
                        onClick={() => { void handleResendInvite(contract); }}
                      >
                        <FiSend aria-hidden="true" />
                        {inviteBusy ? t('Resending…') : t('Resend invite')}
                      </button>
                    ) : portalActive ? null : (
                      <button
                        type="button"
                        className="btn btn--primary"
                        disabled={inviteBusy || !contract.contact_email}
                        title={contract.contact_email ? undefined : t('Add a contact email before sending the invite')}
                        onClick={() => { void handleSendInvite(contract); }}
                      >
                        <FiSend aria-hidden="true" />
                        {inviteBusy ? t('Sending…') : t('Send invite')}
                      </button>
                    )}

                    <button
                      type="button"
                      className="btn btn--secondary"
                      disabled={isArchived}
                      onClick={() => openSiteBuilderForClient(contract)}
                    >
                      <FiPlus aria-hidden="true" />
                      {t('Add site')}
                    </button>

                    <button
                      type="button"
                      className="btn btn--secondary"
                      disabled={isArchived}
                      onClick={() => openSitesForClient(contract)}
                    >
                      {t('Open Sites')}
                    </button>

                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
      )}

      {tab === 'sites' && (
      <div className="page sites-flow site-board">
        <div className="page__header">
          <div>
            <div className="sites-flow__eyebrow">{t('Site directory')}</div>
            <h1 className="page__title">{t('Sites')}</h1>
            <p className="sites-flow__lead">
              {t('Every location you cover, with its boundary, cover window and duty rules on one screen.')}
            </p>
          </div>
          <div className="sites__header-actions">
            <button type="button" className="btn btn--secondary" onClick={() => { void loadSites(); }}>
              <FiRefreshCw aria-hidden="true" />
              {t('Refresh')}
            </button>
            <div className="site-board__new" ref={newSitePicker.rootRef}>
              <button
                ref={newSitePicker.triggerRef}
                type="button"
                className="btn btn--primary"
                onClick={handleCreateSiteFromBoard}
                disabled={boardClientIsArchived}
                aria-haspopup={boardNeedsClientChoice ? 'dialog' : undefined}
                aria-expanded={boardNeedsClientChoice ? newSitePicker.isOpen : undefined}
              >
                <FiPlus aria-hidden="true" />
                {t('Create new site')}
              </button>
              {newSitePicker.isOpen && (
                <OptionPicker
                  className="site-board__new-picker"
                  label={t('Which client is this site for?')}
                  showLabel
                  options={newSiteClientOptions}
                  onSelect={handleNewSiteClientChosen}
                  onCancel={() => newSitePicker.close()}
                  searchPlaceholder={t('Find a client…')}
                  emptyMessage={t('No clients match')}
                />
              )}
            </div>
          </div>
        </div>

        {(clientError || clientSuccess) && (
          <div className="site-board__banners">
            {clientError && !clientModal && <div className="msg--error">{clientError}</div>}
            {clientSuccess && <div className="msg--success">{clientSuccess}</div>}
          </div>
        )}

        <div className="site-board__signals">
          <button
            type="button"
            className="site-board__signal"
            aria-pressed={siteStatusFilter === 'all'}
            onClick={() => setSiteStatusFilter('all')}
          >
            <span className="site-board__signal-label"><FiMapPin aria-hidden="true" />{t('Sites')}</span>
            <span className="site-board__signal-value">{sites.length}</span>
            <span className="site-board__signal-note">
              {tn('across {count} client', 'across {count} clients', clientsWithSitesCount)}
            </span>
          </button>
          <button
            type="button"
            className="site-board__signal"
            aria-pressed={siteStatusFilter === 'round-clock'}
            onClick={() => setSiteStatusFilter(siteStatusFilter === 'round-clock' ? 'all' : 'round-clock')}
          >
            <span className="site-board__signal-label"><FiClock aria-hidden="true" />{t('Round-the-clock')}</span>
            <span className="site-board__signal-value">{roundTheClockCount}</span>
            <span className="site-board__signal-note">{t('Manned 24 hours a day')}</span>
          </button>
          <button
            type="button"
            className="site-board__signal"
            aria-pressed={siteStatusFilter === 'fixed'}
            onClick={() => setSiteStatusFilter(siteStatusFilter === 'fixed' ? 'all' : 'fixed')}
          >
            <span className="site-board__signal-label"><FiSunset aria-hidden="true" />{t('Fixed window')}</span>
            <span className="site-board__signal-value">{sites.length - roundTheClockCount}</span>
            <span className="site-board__signal-note">{t('Cover between set hours')}</span>
          </button>
          <button
            type="button"
            className={`site-board__signal site-board__signal--attention${attentionCount > 0 ? ' site-board__signal--flagged' : ''}`}
            aria-pressed={siteStatusFilter === 'attention'}
            onClick={() => setSiteStatusFilter(siteStatusFilter === 'attention' ? 'all' : 'attention')}
          >
            <span className="site-board__signal-label"><FiAlertTriangle aria-hidden="true" />{t('Needs setup')}</span>
            <span className="site-board__signal-value">{attentionCount}</span>
            <span className="site-board__signal-note">{t('Missing a pin, boundary or address')}</span>
          </button>
        </div>

        <div className="site-board__toolbar">
          <div className="site-board__search">
            <FiSearch aria-hidden="true" />
            <input
              type="search"
              className="form__input"
              value={siteSearch}
              onChange={(event) => setSiteSearch(event.target.value)}
              placeholder={t('Search sites, addresses or clients…')}
              aria-label={t('Search sites, addresses or clients…')}
            />
          </div>
          <SiteClientFilter
            options={siteClientFilterOptions}
            value={siteClientFilter}
            totalSiteCount={sites.length}
            onChange={setSiteClientFilter}
          />
          {hasBoardFilters && (
            <button type="button" className="btn btn--secondary btn--sm" onClick={clearBoardFilters}>
              {t('Clear filters')}
            </button>
          )}
          {onManageClients && (
            <button type="button" className="site-board__handoff" onClick={onManageClients}>
              <FiUsers aria-hidden="true" />
              {t('Need to add or edit clients?')}
            </button>
          )}
        </div>

        {boardGroups.length === 0 ? (
          <div className="site-board__empty">
            <div className="site-board__empty-icon"><FiMapPin size={26} aria-hidden="true" /></div>
            <h2>{sites.length === 0 ? t('No sites yet') : t('Nothing matches these filters')}</h2>
            <p>
              {sites.length === 0
                ? t('A site holds the boundary guards are judged against, the hours it must be manned, and the licence its duties require.')
                : t('Try a different client, clear the search, or reset the filters to see every site again.')}
            </p>
            {sites.length === 0 ? (
              assignableClients.length > 0 ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={handleCreateSiteFromBoard}
                  aria-label={t('Add the first site')}
                >
                  <FiPlus aria-hidden="true" />
                  {t('Add site')}
                </button>
              ) : (
                <>
                  <p className="site-board__empty-hint">{t('Create a client contract first — every site is linked to one.')}</p>
                  {onManageClients && (
                    <button type="button" className="site-board__handoff" onClick={onManageClients}>
                      <FiUsers aria-hidden="true" />
                      {t('Need to add or edit clients?')}
                    </button>
                  )}
                </>
              )
            ) : (
              <button type="button" className="btn btn--secondary" onClick={clearBoardFilters}>
                {t('Clear filters')}
              </button>
            )}
          </div>
        ) : boardGroups.map((group) => {
          const groupContract = group.contract;
          const groupArchived = groupContract?.status === 'archived';

          return (
            <section
              key={group.key}
              className={`site-board__group${groupContract ? '' : ' site-board__group--orphan'}`}
            >
              <div className="site-board__group-head">
                <div className="site-board__group-title">
                  <h2>{groupContract ? groupContract.name : t('No client linked')}</h2>
                  {groupContract && (
                    <span className={`sites-flow__status sites-flow__status--${groupContract.status}`}>
                      {getInviteStatusLabel(groupContract)}
                    </span>
                  )}
                </div>
                <div className="site-board__group-meta">
                  <span>{tn('{count} site', '{count} sites', group.sites.length)}</span>
                  {groupContract?.contact_name && (
                    <span><FiUser aria-hidden="true" />{groupContract.contact_name}</span>
                  )}
                  {groupContract?.contact_email && (
                    <span><FiMail aria-hidden="true" />{groupContract.contact_email}</span>
                  )}
                  {!groupContract && <span>{t('These sites predate client contracts and are hidden from the client portal.')}</span>}
                </div>
                {groupContract && !groupArchived && (
                  <button
                    type="button"
                    className="btn btn--secondary btn--sm site-board__group-action"
                    aria-label={t('Add a site to {name}', { name: groupContract.name })}
                    onClick={() => openSiteBuilderForClient(groupContract)}
                  >
                    <FiPlus aria-hidden="true" />
                    {t('Add site')}
                  </button>
                )}
              </div>

              <div className="site-board__grid">
                {group.sites.map((site) => {
                  const attention = getSiteAttention(site);
                  const coverage = getCoverageSegments(site);
                  const countryName = site.country_code ? getCountryName(site.country_code) : null;

                  return (
                    <article
                      key={site.id}
                      className={`site-tile${attention.length > 0 ? ' site-tile--attention' : ''}${groupArchived ? ' site-tile--archived' : ''}`}
                    >
                      <div className="site-tile__head">
                        <h3 className="site-tile__name">{site.name}</h3>
                        <p className="site-tile__address">
                          <FiMapPin aria-hidden="true" />
                          <span>
                            {site.address || <em>{t('No address on file')}</em>}
                            {countryName && <span className="site-tile__country"> · {countryName}</span>}
                          </span>
                        </p>
                      </div>

                      {attention.length > 0 && (
                        <ul className="site-tile__flags">
                          {attention.map((flag) => (
                            <li key={flag} className="site-tile__flag">
                              <FiAlertTriangle aria-hidden="true" />
                              {flag}
                            </li>
                          ))}
                        </ul>
                      )}

                      <div className="site-tile__coverage">
                        <div className="site-tile__coverage-head">
                          <span>{t('Required coverage')}</span>
                          <strong>{formatCoverageWindow(site)}</strong>
                        </div>
                        <div
                          className="site-tile__track"
                          role="img"
                          aria-label={t('Cover window {window}', { window: formatCoverageWindow(site) })}
                        >
                          {coverage.map((segment) => (
                            <span
                              key={`${segment.left}-${segment.width}`}
                              className="site-tile__fill"
                              style={{ left: `${segment.left}%`, width: `${segment.width}%` }}
                            />
                          ))}
                        </div>
                        <div className="site-tile__scale" aria-hidden="true">
                          <span>00</span><span>06</span><span>12</span><span>18</span><span>24</span>
                        </div>
                      </div>

                      <dl className="site-tile__facts">
                        <div className="site-tile__fact">
                          <dt>{t('Geofence')}</dt>
                          <dd>{getSiteGeofenceSummary(site)}</dd>
                        </div>
                        <div className="site-tile__fact">
                          <dt>{t('Welfare checks')}</dt>
                          <dd>{getSiteWelfareSummary(site)}</dd>
                        </div>
                        <div className="site-tile__fact">
                          <dt>{t('Timezone')}</dt>
                          <dd>{resolveSiteTimeZone(site)}</dd>
                        </div>
                        <div className="site-tile__fact">
                          <dt>{t('Minimum guards')}</dt>
                          <dd>{site.required_guard_count ?? 1}</dd>
                        </div>
                        <div className="site-tile__fact">
                          <dt>{t('Required licence')}</dt>
                          <dd>{site.required_licence?.name || <em>{t('Not set')}</em>}</dd>
                        </div>
                      </dl>

                      <div className="site-tile__actions">
                        <button
                          type="button"
                          className="btn btn--primary btn--sm"
                          onClick={() => beginSiteEdit(site)}
                        >
                          {t('Edit site')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => beginSiteEdit(site, 'boundary')}
                        >
                          {t('Edit boundary')}
                        </button>
                        <button
                          type="button"
                          className="btn btn--secondary btn--sm"
                          onClick={() => setPostOrdersSite({ id: site.id, name: site.name })}
                        >
                          {t('Post orders')}
                        </button>
                        {showTabNavigation && (
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={() => openPatrolsForSite(site)}
                          >
                            {t('Patrols')}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn--icon site-tile__delete"
                          aria-label={t('Delete {name}', { name: site.name })}
                          title={t('Delete site')}
                          onClick={() => setDeleteConfirmSiteId(site.id)}
                        >
                          <FiTrash2 aria-hidden="true" />
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}

        {sites.length > 0 && clientsWithoutSites.length > 0 && (
          <div className="site-board__pending">
            <div className="site-board__pending-head">
              <h2>{t('Waiting on their first site')}</h2>
              <p>{t('These clients are signed up but have nowhere to roster guards yet.')}</p>
            </div>
            <div className="site-board__pending-list">
              {clientsWithoutSites.map((contract) => (
                <button
                  key={contract.id}
                  type="button"
                  className="site-board__pending-item"
                  aria-label={t('Add a site to {name}', { name: contract.name })}
                  onClick={() => openSiteBuilderForClient(contract)}
                >
                  {contract.name}
                  <FiPlus aria-hidden="true" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      )}

      {siteBuilderOpen && (editingSite || selectedClient) && (
        <SiteBuilder
          key={editingSite?.id ?? 'new-site'}
          client={editingSite ? (selectedClient ?? null) : selectedClient}
          site={editingSite}
          initialStep={builderInitialStep}
          onClose={handleCloseSiteBuilder}
          onSaved={handleSiteSaved}
        />
      )}

      {clientModal && (
        <ClientModal
          key={clientModal.mode === 'edit' ? clientModal.contract.id : 'create'}
          mode={clientModal.mode}
          initial={clientModal.mode === 'edit' ? clientModal.contract : null}
          defaultCountryCode={companyCountryCode}
          isSubmitting={isSubmittingClient}
          error={clientError}
          onSubmit={(values) => { void handleSubmitClient(values); }}
          onClose={closeClientModal}
        />
      )}

      {deleteConfirmSiteId && (
        <ConfirmDeleteModal
          title={t('Delete site')}
          message={t('Are you sure? This will permanently delete the site and all associated data (checkpoints, patrols, posts, duty slots, and shifts). This action cannot be undone.')}
          isDeleting={isDeleting}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteConfirmSiteId(null)}
        />
      )}

      {postOrdersSite && (
        <PostOrdersModal
          siteId={postOrdersSite.id}
          siteName={postOrdersSite.name}
          onClose={() => setPostOrdersSite(null)}
        />
      )}

      {portalSettingsContract && (
        <ClientPortalSettingsModal
          clientContractId={portalSettingsContract.id}
          clientName={portalSettingsContract.name}
          onClose={() => setPortalSettingsContract(null)}
        />
      )}

      {clientLifecycleTarget && (
        <ClientLifecycleModal
          target={clientLifecycleTarget}
          isSubmitting={isSubmittingClientLifecycle}
          pendingAction={clientLifecyclePendingAction}
          error={clientLifecycleError}
          onConfirm={() => { void handleConfirmClientLifecycle(); }}
          onArchiveInstead={clientLifecycleTarget.action === 'delete'
            ? () => { void handleArchiveInsteadFromDelete(); }
            : undefined}
          onCancel={() => {
            if (!isSubmittingClientLifecycle) {
              setClientLifecycleTarget(null);
              setClientLifecycleError('');
              setClientLifecyclePendingAction(null);
            }
          }}
        />
      )}

    </div>
  );
}
